import { useState } from 'react';
import { __, sprintf } from '../i18n/vsme';
import { toFileStoreError, type FileStoreApi, type KeptLabels } from './fileStoreApi';
import type { LabelOptions } from './fileStoreTypes';

type KeptLabelsNoticeProps = {
  api: FileStoreApi;
  ownerRecordId: number;
  items: KeptLabels[];
  labelOptions: LabelOptions;
  /** The user settled one file: `applied` = relabelled with the deposit's labels, else kept as stored. */
  onResolved: (fileId: number, applied: boolean) => void;
};

type ItemProps = Omit<KeptLabelsNoticeProps, 'items'> & { item: KeptLabels };

/** « Type – Year[ – Entity] »: the entity only when the two sides name different ones. */
const labelLine = (type: string, year: number | null, entity: string | null): string => (
  [type, year === null ? null : String(year), entity].filter((part): part is string => part !== null).join(' – ')
);

const KeptLabelsItem = ({ api, ownerRecordId, item, labelOptions, onResolved }: ItemProps) => {
  const [phase, setPhase] = useState<'idle' | 'applying'>('idle');
  const [error, setError] = useState<string | null>(null);
  const { file, depositedName, requested } = item;

  const entityDiffers = file.entity.id !== requested.entity_record_id;
  const requestedEntity = labelOptions.entities.find((entity) => entity.id === requested.entity_record_id)?.label
    ?? (requested.entity_record_id === null ? __('Siège social') : String(requested.entity_record_id));
  const requestedType = labelOptions.types.find((type) => type.key === requested.type_key)?.label ?? requested.type_key;
  const stored = labelLine(file.type_label, file.concerned_year, entityDiffers ? file.entity.label : null);
  const wanted = labelLine(requestedType, requested.concerned_year, entityDiffers ? requestedEntity : null);
  const where = file.name === depositedName
    ? sprintf(__('« %1$s » est déjà dans ce dossier'), depositedName)
    : sprintf(__('« %1$s » est déjà dans ce dossier (« %2$s »)'), depositedName, file.name);

  const apply = async () => {
    setPhase('applying');
    setError(null);
    try {
      await api.patch(ownerRecordId, file.id, requested);
      onResolved(file.id, true);
    } catch (patchError) {
      setError(toFileStoreError(patchError).message);
      setPhase('idle');
    }
  };

  return (
    <li className="file-store-kept-labels__item">
      <p className="file-store-kept-labels__text">
        {sprintf(__('%1$s, classé « %2$s ». Vos libellés n’ont pas été appliqués.'), where, stored)}
      </p>
      {error !== null && <p className="file-store-kept-labels__error" role="alert">{error}</p>}
      <div className="file-store-kept-labels__actions">
        <button
          type="button"
          className="btn-secondary"
          disabled={phase === 'applying'}
          onClick={() => {
            void apply();
          }}
        >
          {sprintf(__('Appliquer « %s »'), wanted)}
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={phase === 'applying'}
          onClick={() => onResolved(file.id, false)}
        >
          {__('Garder')}
        </button>
      </div>
    </li>
  );
};

/**
 * Deposited bytes the owner already held: the server keeps the stored file's labels (dedup rule,
 * CompanyFileStore.md « Deposit »). Each file says which labels it kept and lets the user apply
 * the ones they chose — a PATCH, refused like any relabel — or keep the stored ones.
 */
const KeptLabelsNotice = ({ items, ...itemProps }: KeptLabelsNoticeProps) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="file-store-kept-labels" role="status" aria-label={__('Fichiers déjà présents')}>
      <ul className="file-store-kept-labels__list">
        {items.map((item) => <KeptLabelsItem key={item.file.id} item={item} {...itemProps} />)}
      </ul>
    </section>
  );
};

export default KeptLabelsNotice;
